import type { BusinessTypes } from '@disco/constants/business-type-v2'
import { STORAGE, USER } from '@disco/constants/billing-provider-components'
import { AUTOALBUM, AUTOALBUM_ORIG_NAME } from '@disco/constants/PlaylistTypes'
import type { SORT, SORT_DIRECTION } from '@disco/constants/SortTypes'
import {
  defaultSortTypes,
  SORT_DIRECTION_ASC,
  TITLE_SORT,
} from '@disco/constants/SortTypes'
import type { JobsType } from '@disco/constants/user-jobs'
import type { ColumnSetting } from '@disco/utils/elementSizes'
import {
  defaultTrackHoverFields,
  defaultTrackPaneFields,
} from '@disco/utils/track-metadata'
import get from 'lodash/get'
import orderBy from 'lodash/orderBy'
import { createSelector } from 'reselect'
import type {
  BusinessData,
  CustomFieldsConfig,
  MultiLevelSetting,
  Plan,
  PrePopulatedSubscriptionData,
} from 'types/business'
import type { CustomContactField } from 'types/list'
import type { State } from 'types/redux'
import type {
  BusinessUser,
  User,
  UserId,
  UserSettings,
  UserStateRequired,
} from 'types/user'
import type { UserAliasSettings } from 'types/user-settings'
import type { UserReducer } from '@disco/state-management/reducers/user'
const emptyArray: any[] = []
const emptyObject: Record<string, any> = {}
// TODO: converge User and UserReducer types
/**
 * Gets the current user object from the state.
 * @param state - The Redux state
 * @returns The user object
 */
export const getUser = (state: State): User => state.user as any
/**
 * Gets the current user's ID.
 * @param state - The Redux state
 * @returns The user ID, or null/undefined if not available
 */
export const getUserId = (state: State): UserId | null | undefined =>
  state.user.id

/**
 * Gets the trial start timestamp for the user's business.
 * @param state - The Redux state
 * @returns The trial start timestamp in seconds, or null if not available
 */
const getTrialStart = (state: State): number | null | undefined => {
  return state.user?.business?.stats?.trial_started_at ?? null
}

/**
 * Gets the trial start date as a Date object.
 * @param state - The Redux state
 * @returns The trial start date, or epoch date if no trial start timestamp
 */
export const getTrialStartDate = createSelector(
  [getTrialStart],
  (timestamp): Date => {
    if (timestamp) return new Date(timestamp * 1000)
    return new Date(0)
  },
)
/**
 * Gets the user's constance settings.
 * @param state - The Redux state
 * @returns Object containing constance settings
 */
export const getConstanceSettings = (state: State): Record<string, any> =>
  state.user?.constance || emptyObject

/**
 * Gets a specific constance setting by key.
 * @param state - The Redux state
 * @param key - The setting key to retrieve
 * @returns The setting value, or undefined if not found
 */
export const getConstanceSetting = (state: State, key: string): any => {
  const settings = getConstanceSettings(state)
  return settings[key]
}
/**
 * Gets the user's settings object.
 * @param state - The Redux state
 * @returns The user settings object, or null if not available
 */
export const getUserSettings = (
  state: State,
): UserSettings | null | undefined => state.user?.settings ?? null
/**
 * Gets the user's metadata aliases configuration.
 * @param state - The Redux state
 * @returns The metadata aliases settings, or null if not available
 */
export const getUserMetadataAliases = (
  state: State,
): UserAliasSettings | null | undefined =>
  get(state.user, 'metadata_aliases', null)
/**
 * Gets the user's channels sort parameter setting.
 * @param state - The Redux state
 * @returns The channels sort parameter, or null if not set
 */
export const getChannelsSortParam = (state: State): string | null | undefined =>
  get(state.user, 'settings.channels_sort_param', null)
/**
 * Gets the user's channels sort direction setting.
 * @param state - The Redux state
 * @returns The channels sort direction, or null if not set
 */
export const getChannelsSortDirection = (
  state: State,
): string | null | undefined =>
  get(state.user, 'settings.channels_sort_direction', null)
/**
 * Gets the user's custom channels order setting.
 * @param state - The Redux state
 * @returns Array of channel IDs in custom order, or null if not set
 */
export const getChannelsCustomOrder = (
  state: State,
): number[] | null | undefined =>
  get(state.user, 'settings.channels_custom_order', null)
/**
 * Gets whether the user has enabled hiding auto playlists.
 * @param state - The Redux state
 * @returns True if auto playlists should be hidden, false otherwise
 */
export const getHideAutoPlaylistsSetting = (state: State): boolean => {
  return get(state.user, 'settings.hide_auto_playlists', false)
}
/**
 * Gets the user's explorer column width setting.
 * @param state - The Redux state
 * @returns The column width setting (e.g., 'Default', 'Compact')
 */
export const getExplorerColumnSetting = (state: State): ColumnSetting => {
  return get(state.user, 'settings.explorer_column_width', 'Default')
}
/**
 * Gets whether the user has enabled hiding inbox removal warnings.
 * @param state - The Redux state
 * @returns True if inbox removal warnings should be hidden, false otherwise
 */
export const getHideInboxRemovalSetting = (state: State): boolean => {
  return get(state.user, 'settings.hide_inbox_removal_warning', false)
}
/**
 * Gets whether the user has enabled hiding contact removal warnings.
 * @param state - The Redux state
 * @returns True if contact removal warnings should be hidden, false otherwise
 */
export const getHideContactRemovalSetting = (state: State): boolean => {
  return get(state.user, 'settings.hide_contact_removal_warning', false)
}
/**
 * Gets the user's explorer component height setting.
 * @param state - The Redux state
 * @returns The component height setting ('Default' or 'Compact')
 */
export const getExplorerComponentHeight = (
  state: State,
): 'Default' | 'Compact' =>
  state.user?.settings?.explorer_component_height ?? 'Default'
/**
 * Gets whether the track metadata 'more' button should be hidden.
 * @param state - The Redux state
 * @returns True if the track metadata more button should be hidden, false otherwise
 */
export const getHideTrackMetadataMoreButton = (state: State): boolean => {
  return get(
    state.user,
    'business.public_metadata.hide_track_metadata_more_button',
    false,
  )
}
/**
 * Parses the track view extra metadata field from string to array format.
 * @param track_view_extra_metadata_field - The metadata field value to parse
 * @returns Array of metadata field names, or null if empty/null
 * @todo Convert `track_view_extra_metadata_field` on server to array and remove `trackViewExtraMetadataFieldParser`
 */
export function trackViewExtraMetadataFieldParser(
  track_view_extra_metadata_field:
    | 'null'
    | ''
    | null
    | (string | null | undefined),
): string[] | null | undefined {
  // TODO: convert `track_view_extra_metadata_field` on server to array and remove `trackViewExtraMetadataFieldParser`
  if (track_view_extra_metadata_field === 'null') {
    return null
  } else if (typeof track_view_extra_metadata_field === 'string') {
    if (track_view_extra_metadata_field === '') {
      return null
    } else {
      return track_view_extra_metadata_field.split(',')
    }
  } else {
    return track_view_extra_metadata_field
  }
}
export const getMultiLevelSetting = {
  //
  // getMultiLevelSetting - experimental type-safe setting reader
  // if it works out at scale, could be refactored to be smaller
  //
  explorer_trackhover_fields(user: UserReducer): MultiLevelSetting<string[]> {
    const settingUser =
      user.settings?.explorer_hover_fields ||
      user.settings?.explorer_trackhover_fields
    let settingBusiness = user.business?.explorer_trackhover_fields
    if (settingBusiness?.length === 0) settingBusiness = undefined
    const settingDefault = defaultTrackHoverFields
    return {
      settingUser: settingUser || settingBusiness || settingDefault,
      settingBusiness: settingBusiness || settingDefault,
      settingDefault: settingDefault,
    }
  },

  explorer_trackpane_fields(
    user: UserReducer | User,
  ): MultiLevelSetting<string[]> {
    const settingUser = user.settings?.explorer_trackpane_fields
    let settingBusiness = user.business?.explorer_trackpane_fields
    if (settingBusiness?.length === 0) settingBusiness = undefined
    const settingDefault = defaultTrackPaneFields
    return {
      settingUser: settingUser || settingBusiness || settingDefault,
      settingBusiness: settingBusiness || settingDefault,
      settingDefault: settingDefault,
    }
  },
}
/**
 * Gets whether the writers tab should be hidden.
 * @param state - The Redux state
 * @returns True if the writers tab should be hidden, false otherwise
 */
export const getHideWritersTab = (state: State): boolean => {
  return get(state.user, 'business.public_metadata.hide_writers_tab', false)
}
/**
 * Gets whether territories feature is enabled for the user's business.
 * @param state - The Redux state
 * @returns True if territories feature is enabled, false otherwise
 */
export const getTerritoriesEnabled = (state: State): boolean =>
  get(state.user, 'business.private_metadata.territories_enabled', false)
/**
 * Gets whether MFA (Multi-Factor Authentication) is enabled for the user's business.
 * @param state - The Redux state
 * @returns True if MFA is enabled, false otherwise
 */
export const getMfaAuthEnabled = (state: State): boolean =>
  get(state.user, 'business.private_metadata.mfa_auth_enabled', false)
/**
 * Gets whether native tags feature is enabled for the user's business.
 * @param state - The Redux state
 * @returns True if native tags feature is enabled, false otherwise
 */
export const isNativeTagsEnabled = (state: State): boolean => {
  return state.user.business?.private_metadata.native_tags_enabled || false
}
/**
 * Gets whether the user's business is on a client libraries trial.
 * @param state - The Redux state
 * @returns True if on client libraries trial, false otherwise
 */
export const isClientLibrariesTrial = (state: State): boolean => {
  return state.user.business?.private_metadata.client_libraries_trial || false
}
/**
 * Gets whether the user has enabled hiding channel removal warnings.
 * @param state - The Redux state
 * @returns True if channel removal warnings should be hidden, false otherwise
 */
export const getHideChannelRemovalSetting = (state: State): boolean => {
  return get(state.user, 'settings.hide_channel_removal_warning', false)
}
/**
 * Gets whether the user has enabled hiding merge warnings.
 * @param state - The Redux state
 * @returns True if merge warnings should be hidden, false otherwise
 */
export const getHideMergeWarningSetting = (state: State): boolean => {
  return get(state.user, 'settings.hide_merge_warning', false)
}
/**
 * Gets the user's counter timestamp setting.
 * @param state - The Redux state
 * @returns The counter timestamp string, or null if not set
 */
export const getUserCounterTimestamp = (
  state: State,
): string | null | undefined => state.user?.settings?.counter_timestamp ?? null
/**
 * Gets the user's channel playlist sort parameter setting.
 * @param state - The Redux state
 * @returns The channel playlist sort parameter (defaults to TITLE_SORT)
 */
export const getChannelPlaylistSortParam = (state: State): SORT =>
  get(state.user, 'settings.channel_playlist_sort_param', TITLE_SORT)
/**
 * Gets the user's channel playlist sort direction setting.
 * @param state - The Redux state
 * @returns The channel playlist sort direction (defaults to SORT_DIRECTION_ASC)
 */
export const getChannelPlaylistSortDirection = (state: State): SORT_DIRECTION =>
  get(
    state.user,
    'settings.channel_playlist_sort_direction',
    SORT_DIRECTION_ASC,
  )
/**
 * Gets the user's chosen search columns setting.
 * @param state - The Redux state
 * @returns Array of search column names (defaults to defaultSortTypes)
 */
export const getChosenSearchColumns = (state: State): string[] =>
  get(state.user, 'settings.search_columns', defaultSortTypes)
/**
 * Gets the custom fields configuration for the user's business.
 * @param state - The Redux state
 * @returns The custom fields configuration object
 */
export const getCustomFieldsConfig = (state: State): CustomFieldsConfig =>
  get(
    state.user,
    'business.public_metadata.extra_metadata',
    get(state.user, 'business.public_metadata.extra_metadata', {}),
  )
/**
 * Gets all custom field keys from the configuration.
 * @param state - The Redux state
 * @returns Array of custom field keys
 */
export const getCustomFieldKeys = createSelector(
  getCustomFieldsConfig,
  fields => {
    return Object.keys(fields)
  },
)
/**
 * Gets only the visible custom fields, sorted by order.
 * @param state - The Redux state
 * @returns Array of visible custom field objects with key and configuration
 */
export const getVisibleCustomFields = createSelector(
  getCustomFieldsConfig,
  fields =>
    Object.entries(fields)
      .filter(([, field]) => field.visible)
      .map(([key, field]) => ({
        key,
        ...field,
      }))
      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0)),
)
/**
 * Gets the keys of visible custom fields.
 * @param state - The Redux state
 * @returns Array of visible custom field keys
 */
export const getVisibleCustomFieldsKeys = createSelector(
  getVisibleCustomFields,
  fields => fields.map(field => field.key),
)
/**
 * Gets custom fields organized into rows based on field sizes.
 * Each row has a maximum width of 12 units. Fields are arranged to fit within rows.
 * @param state - The Redux state
 * @returns Array of field rows, where each row is an array of custom field objects, or null if no fields
 */
export const getCustomFields = createSelector(
  getVisibleCustomFields,
  fields => {
    if (fields.length === 0) {
      return null
    }

    const rows: (typeof fields)[] = []
    fields.forEach(field => {
      if (rows.length > 0) {
        const currentRow = rows[rows.length - 1]!
        const remainingSpace =
          12 -
          currentRow.reduce((acc, field) => acc + (field.field_size ?? 12), 0)

        if (remainingSpace >= (field.field_size ?? 12)) {
          currentRow.push(field)
        } else {
          rows.push([field])
        }
      } else {
        rows.push([field])
      }
    })
    return rows
  },
)
/**
 * Gets the user's permissions array.
 * @param state - The Redux state
 * @returns Array of permission strings
 */
export const getPermissions = (state: State): string[] => {
  return get(state.user, 'permissions', [])
}
/**
 * Gets the user's business settings.
 * @param state - The Redux state
 * @returns Partial business data object
 */
export const getBusinessSettings = (state: State): Partial<BusinessData> => {
  return state.user?.business ?? {}
}
/**
 * Gets business email templates for sharing.
 * @param state - The Redux state
 * @returns Array of email template objects with id and name, or null if not available
 */
export const getBusinessEmailTemplates = (
  state: State,
):
  | {
      id: string
      name: string
    }[]
  | null
  | undefined => {
  return get(state.user, 'business.share_templates', null)
}
/**
 * Gets the business default ZIP mode setting.
 * @param state - The Redux state
 * @returns The default ZIP mode ('optimized' or 'original_else_optimized'), or null if not set
 */
export const getBusinessDefaultZipMode = (
  state: State,
): ('optimized' | 'original_else_optimized') | null | undefined => {
  return state.user?.business?.public_metadata?.default_zip_mode
}

/**
 * Gets the playlist types from the entities state.
 * @param state - The Redux state
 * @returns Array of playlist type entities
 */
const getPlaylistTypes = (state: State) => state.entities.playlistTypes

/**
 * Gets the list of playlist IDs to exclude based on user settings.
 * If hide_auto_playlists is enabled, returns IDs of auto-generated playlists to exclude.
 * @param state - The Redux state
 * @returns Array of playlist IDs to exclude, or null if no exclusions
 */
const NO_EXCLUSIONS: string[] = []
export const getPlaylistExclusionList = createSelector(
  [getUserSettings, getPlaylistTypes],
  (userSettings, playlistTypes) => {
    if (!userSettings?.hide_auto_playlists) return NO_EXCLUSIONS

    const filtered = playlistTypes
      .filter(t => t.name === AUTOALBUM || t.name === AUTOALBUM_ORIG_NAME)
      .map(t => String(t.id))
    return filtered.length > 0 ? filtered : NO_EXCLUSIONS
  },
)
/**
 * Gets the track text display settings based on business configuration.
 * @param state - The Redux state
 * @returns 'title_composer_description' if composer should be used instead of artist, 'default' otherwise
 */
export const getTrackTextSettings = createSelector(
  [getBusinessSettings],
  businessSettings => {
    return businessSettings?.public_metadata
      ?.use_composer_instead_of_artist_in_explorer
      ? 'title_composer_description'
      : 'default'
  },
)
/**
 * Gets the custom contact fields for the user's business.
 * @param state - The Redux state
 * @returns Array of custom contact field configurations
 */
export const getContactCustomFields = (state: State): CustomContactField[] =>
  get(state.user, 'business.contact_custom_fields', emptyArray)
export type sortedBusinessUsers = {
  loading: boolean
  loaded: boolean
  loadError?: unknown | null
  users: BusinessUser[]
}
/**
 * Gets business users sorted by current user first, then by type, then by ID.
 * @param state - The Redux state
 * @returns Object containing loading state and sorted business users array
 */
export const getBusinessUsers = createSelector(
  [
    (state: State) => state.entities.businessUsers,
    (state: State) => state.user.id,
  ],
  (businessUsers, currentUserId): sortedBusinessUsers => {
    const isCurrentUser = (bu: BusinessUser) => bu.user.id === currentUserId

    const users = orderBy<BusinessUser>(
      Object.values(businessUsers.users),
      [
        u => !isCurrentUser(u), // 1. first - the current user, then - the rest
        'type', // 2. first - "administrator", then - "user"
        'id', // 3. first - older users, then - newer users
      ],
      'asc',
    )
    return { ...businessUsers, users }
  },
)
/**
 * Gets the core plan from the user's business metadata.
 * @param state - The Redux state
 * @returns The core plan object, or null/undefined if not found
 */
export const getCorePlan = (state: State): Plan | null | undefined => {
  const { plan, plans = [] } = state.user?.business?.private_metadata || {}
  return [plan, ...plans].filter(Boolean).find(({ slot }) => slot === 'core')
}
/**
 * Gets all plan IDs from the user's business metadata.
 * @param state - The Redux state
 * @returns Array of plan ID strings
 */
export const getAllPlanIds = (state: State): string[] => {
  const { plans = [] } = state.user?.business?.private_metadata || {}
  return plans.filter(Boolean).map(p => p.id)
}
/**
 * Gets the DS (Data Science) quota limit for the user.
 * Calculates the maximum between client libraries track quota and auto tagging tracks limit.
 * @param state - The Redux state with required user data
 * @returns The DS quota limit, or Infinity if unlimited
 * @see https://app.asana.com/0/0/1200878373457006/f
 */
export const getDSQuota = createSelector(
  [
    (state: UserStateRequired) =>
      state.user?.business?.private_metadata
        ?.client_libraries_track_quota_max_allowed,
    (state: UserStateRequired) =>
      state.user?.business?.auto_tagging_tracks_limit,
  ],
  (client_libraries_track_quota_max_allowed, auto_tagging_tracks_limit) => {
    // The logic is described in the task https://app.asana.com/0/0/1200878373457006/f
    let quota = 0

    if (
      client_libraries_track_quota_max_allowed !== null &&
      auto_tagging_tracks_limit !== null
    ) {
      quota = Math.max(
        client_libraries_track_quota_max_allowed ?? 0,
        auto_tagging_tracks_limit ?? 0,
      )
    }

    if (auto_tagging_tracks_limit === null) {
      quota = client_libraries_track_quota_max_allowed || 0
    }

    if (client_libraries_track_quota_max_allowed === null) {
      quota = auto_tagging_tracks_limit || 0
    }

    if (
      auto_tagging_tracks_limit === null &&
      client_libraries_track_quota_max_allowed === null
    ) {
      // "unlimited"
      quota = Infinity
    }

    return quota
  },
)
/**
 * Gets the DS (Data Science) quota used by the user.
 * Calculates the maximum between client libraries track quota used and auto tagging tracks sent.
 * @param state - The Redux state
 * @returns The DS quota used amount
 */
export const getDSQuotaUsed = createSelector(
  [
    (state: State) =>
      state.user?.business?.private_metadata?.client_libraries_track_quota_used,
    (state: State) =>
      state.user?.business?.private_metadata
        ?.client_libraries_track_quota_max_allowed,
    (state: State) => state.user?.business?.auto_tagging_tracks_sent,
    (state: State) => state.user?.business?.auto_tagging_tracks_limit,
  ],
  (
    client_libraries_track_quota_used,
    client_libraries_track_quota_max_allowed,
    auto_tagging_tracks_sent,
    auto_tagging_tracks_limit,
  ) => {
    let quotaUsed = 0

    if (
      auto_tagging_tracks_limit === null &&
      client_libraries_track_quota_used
    ) {
      quotaUsed = client_libraries_track_quota_used
    }

    if (
      client_libraries_track_quota_max_allowed === null &&
      auto_tagging_tracks_sent
    ) {
      quotaUsed = auto_tagging_tracks_sent
    }

    if (
      client_libraries_track_quota_max_allowed !== null &&
      auto_tagging_tracks_limit !== null
    ) {
      quotaUsed = Math.max(
        client_libraries_track_quota_used || 0,
        auto_tagging_tracks_sent || 0,
      )
    }

    return quotaUsed
  },
)
/**
 * Gets the current number of active users in the business.
 * @param state - The Redux state
 * @returns The number of active (non-removed) users
 */
export const getNumberOfUsers = (state: State): number => {
  return (
    getBusinessUsers(state).users.filter(({ removed }) => !removed).length ||
    state.user?.business?.stats?.number_of_users ||
    0
  )
}
/**
 * Gets the total number of tracks in the business.
 * @param state - The Redux state
 * @returns The total track count
 */
export const getNumberOfTracks = (state: State): number => {
  return state?.user?.business?.stats?.total_track_count || 0
}
/**
 * Gets the users quota limit for the business.
 * @param state - The Redux state
 * @returns The users quota limit, or Infinity if unlimited
 */
export const getUsersQuota = (state: State): number => {
  const usersQuota =
    state.user?.business?.private_metadata?.enterprise_user_allowance

  if (isNaN(parseInt(usersQuota))) {
    return Infinity
  }

  return Number(usersQuota)
}
/**
 * Gets the tracks quota limit for the business.
 * @param state - The Redux state
 * @returns The tracks quota limit, or Infinity if unlimited
 */
export const getTracksQuota = (state: State): number => {
  const tracksQuota =
    state.user?.business?.private_metadata?.enterprise_track_allowance

  if (isNaN(parseInt(tracksQuota))) {
    return Infinity
  }

  return Number(tracksQuota)
}
export type UsersLimits = {
  current: number
  max: number
  maxAsDisplayString: string
  overQuota: boolean
  updating: boolean
  unlimitedQuota: boolean
}
/**
 * Checks if the 'don't block invites when over user quota' period has expired.
 * @param state - The Redux state
 * @returns True if the period has expired, false otherwise
 */
export function isDontBlockInvitesExpired(state: State): boolean {
  const dontBlockInvitesWhenOverUserQuotaUntil =
    state?.user?.business?.private_metadata
      ?.dont_block_invites_when_over_user_quota_until

  if (!dontBlockInvitesWhenOverUserQuotaUntil) {
    return false
  }

  const endDate = new Date(dontBlockInvitesWhenOverUserQuotaUntil * 1000)
  const currentDate = new Date()
  return currentDate > endDate
}
/**
 * Gets whether invites should not be blocked when over user quota.
 * @param state - The Redux state
 * @returns True if invites should not be blocked despite being over quota, false otherwise
 */
export const getDontBlockInvitesWhenOverUserQuota = createSelector(
  [isDontBlockInvitesExpired, getUser],
  (dontBlockInvitesExpired, user) => {
    return (
      user?.business?.private_metadata
        ?.dont_block_invites_when_over_user_quota && !dontBlockInvitesExpired
    )
  },
)
/**
 * Gets the users limits information including current usage, quota, and status.
 * @param state - The Redux state
 * @returns Object containing users limit data with current count, max quota, display string, over quota status, updating state, and unlimited quota flag
 */
export const getUsersLimits = createSelector(
  [
    getNumberOfUsers,
    getUsersQuota,
    getDontBlockInvitesWhenOverUserQuota,
    state => state.seatsAndTracks,
  ],
  (numberOfUsers, usersQuota, dontBlockInvitesWhenOverseat, seatsAndTracks) => {
    const unlimitedQuota = usersQuota === Infinity
    return {
      current: numberOfUsers,
      max: usersQuota,
      maxAsDisplayString: String(
        unlimitedQuota ? 'unlimited' : usersQuota.toLocaleString(),
      ),
      overQuota: numberOfUsers >= usersQuota && !dontBlockInvitesWhenOverseat,
      updating: seatsAndTracks.items[USER],
      unlimitedQuota,
    }
  },
)
/**
 * Gets the tracks limits information including current usage, quota, and status.
 * @param state - The Redux state
 * @returns Object containing tracks limit data with current count, max quota, display string, over quota status, updating state, and unlimited quota flag
 */
export const getTracksLimits = createSelector(
  [getNumberOfTracks, getTracksQuota, state => state.seatsAndTracks],
  (numberOfTracks, tracksQuota, seatsAndTracks) => {
    const unlimitedQuota = tracksQuota === Infinity
    return {
      current: numberOfTracks,
      max: tracksQuota,
      maxAsDisplayString: String(
        unlimitedQuota ? 'unlimited' : tracksQuota.toLocaleString(),
      ),
      overQuota: numberOfTracks >= tracksQuota,
      updating: seatsAndTracks.items[STORAGE],
      unlimitedQuota,
    }
  },
)
/**
 * Gets all admin users from the business users list.
 * @param state - The Redux state
 * @returns Array of business users with administrator type
 */
export const getAdminUsers: (state: State) => BusinessUser[] = createSelector(
  [getBusinessUsers],
  businessUsers => {
    return businessUsers.users.filter(({ type }) => type === 'administrator')
  },
)
/**
 * Checks if the user is a bank draft subscriber.
 * @param state - The Redux state
 * @returns True if the user is a bank draft subscriber, false otherwise
 */
export const isBankDraftSubscriber: (state: State) => boolean = createSelector(
  [getUser],
  user => {
    return (
      user?.business?.private_metadata?.plan_type === 'bank_draft_subscriber'
    )
  },
)
/**
 * Checks if the artist plan signup has been sent.
 * @param state - The Redux state
 * @returns True if artist plan signup has been sent, false otherwise
 */
export const getArtistPlanSignupSent: (state: State) => boolean =
  createSelector([getUser], user => {
    return user?.business?.private_metadata?.artist_plan_signup_sent || false
  })
/**
 * Checks if the privacy policy dialog should be shown.
 * Currently checks for Tidal partner source and whether privacy policy has been accepted.
 * @param state - The Redux state
 * @returns True if privacy policy dialog should be shown, false otherwise
 */
export const isShowPrivacyPolicyDialog = (state: State): boolean => {
  const user = state.user
  return Boolean(
    // TODO: BE + FE, update if supported more then one partner_source
    user.business?.partner_source?.toLowerCase() === 'tidal' &&
      !user.business?.tidal_privacy_policy_accepted_at,
  )
}
/**
 * Checks if the audio separation terms have been accepted.
 * This covers both Instant Instrumental and Instant A Cappella features.
 *
 * Note: the legacy `instant_instrumental_terms_accepted_at` key is intentionally
 * NOT checked here. All users must accept the updated unified terms, even if they
 * previously accepted the old Instrumental-only terms.
 *
 * @param state - The Redux state
 * @returns True if audio separation terms have been accepted, false otherwise
 */
export const isAudioSeparationTermsAccepted = (state: State): boolean => {
  const settings = state.user.settings
  return Boolean(settings?.audio_separation_terms_accepted_at)
}

/**
 * @deprecated Use isAudioSeparationTermsAccepted instead
 */
export const isInstantInstrumentalTermsAccepted = isAudioSeparationTermsAccepted
/**
 * Checks if the lyric transcription terms have been accepted.
 * @param state - The Redux state
 * @returns True if lyric transcription terms have been accepted, false otherwise
 */
export const isLyricTranscriptionTermsAccepted = (state: State): boolean => {
  return Boolean(state.user.settings?.lyric_transcription_terms_accepted_at)
}
/**
 * Gets the pre-populated subscription data for the user's business.
 * @param state - The Redux state
 * @returns The pre-populated subscription data object
 */
export const getPrePopulatedSubscriptionData = (
  state: State,
): PrePopulatedSubscriptionData => {
  return state?.user?.business?.pre_populated_subscription_data || {}
}
/**
 * Gets the business type (v2) for the user's business.
 * @param state - The Redux state
 * @returns The business type v2, or null if not available
 */
export const getBusinessTypeV2 = (state: State): BusinessTypes => {
  return state?.user?.business?.type_v2 || null
}
/**
 * Gets the user's job type from business user settings.
 * @param state - The Redux state
 * @returns The user's job type, or null if not available
 */
export const getUserJob = (state: State): JobsType => {
  return state?.user?.business_user_settings?.job || null
}
